import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import type { Pedido } from '@abastece/core';
import { db } from '../db/dexie.js';
import { useT } from '../i18n/index.js';
import { precioDe } from '../lib/catalogo.js';
import { anunciar } from '../lib/hecho.js';
import { lineasParaCambiar, type LineaPedido } from '../lib/pedidos.js';
import { anterior, volver } from '../lib/volver.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { TomarPedido } from '../ui/TomarPedido.js';

/**
 * «Editar pedido»: an order still to deliver, changed on the screen it was taken on (the owner,
 * 2026-10-15: «We need to be able to edit orders in the order screen»), opened from «Editar» on the
 * list of orders, which it goes back to once saved, with the order marked there. Delivered or
 * cancelled meanwhile, on this phone or another, it says so instead: what was delivered is a sale,
 * and is put right as a sale is.
 */
export function EditarPedido() {
  const t = useT();
  const navigate = useNavigate();
  const { pedidoId } = useParams({ from: '/clientes/pedidos/$pedidoId/editar' });
  /** Null while it is read; undefined when this phone has no such order. */
  const pedido = useLiveQuery(async () => (await db.pedidos.get(pedidoId)) ?? undefined, [pedidoId], null);
  const productos = useLiveQuery(() => db.productos.toArray(), [], null);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], null);
  const precios = useLiveQuery(() => db.precios.toArray(), [], null);
  /** The order as the screen opens on it, and its lines: read once, so what is typed is not reset under the hands. */
  const [inicio, setInicio] = useState<{ pedido: Pedido; lineas: LineaPedido[] | null } | null>(null);
  useEffect(() => {
    if (inicio || !pedido || pedido.estado !== 'pendiente' || !productos || !unidades || !precios) return;
    const lineas = lineasParaCambiar(pedido, { productos, unidades, precioDe: (u, lista) => precioDe(precios, u, lista ?? pedido.listaId) });
    setInicio({ pedido, lineas });
  }, [inicio, pedido, productos, unidades, precios]);

  async function guardado(id: string) {
    if (anterior()?.pathname === '/pedidos') {
      // Back to the list as it was, then the order marked on the pending ones, where it is.
      await volver({ to: '/pedidos' });
      void navigate({ to: '/pedidos', search: { tomado: id }, replace: true });
      return;
    }
    const p = await db.pedidos.get(id);
    const quien = p ? await db.clientes.get(p.clienteId) : undefined;
    anunciar(t('pedidos.cambiadoPara', { cliente: quien?.nombre ?? t('pos.unCliente') }));
    void volver({ to: '/pedidos', search: { tomado: id } });
  }

  /** Why it cannot be changed here, when it cannot. */
  const noSePuede =
    pedido === undefined
      ? t('pedidos.cambiarNoEsta')
      : pedido && pedido.estado === 'entregado'
        ? t('pedidos.cambiarEntregado')
        : pedido && pedido.estado === 'cancelado'
          ? t('pedidos.cambiarCancelado')
          : inicio && inicio.lineas === null
            ? t('pedidos.cambiarSinProducto')
            : null;

  return (
    <Pagina titulo={t('pedidos.editarTitulo')} volverA="/pedidos">
      {noSePuede ? (
        <div data-pedido-no-se-cambia>
          <Vacio mensaje={noSePuede} icono="pedidos" />
        </div>
      ) : (
        inicio?.lineas && (
          <TomarPedido pedido={inicio.pedido} lineasIniciales={inicio.lineas} onGuardado={(id) => void guardado(id)} />
        )
      )}
    </Pagina>
  );
}
